import { styled } from 'ds';
import { Link } from 'ds/components/link';
import { colorToAlpha } from 'helpers/insight';
import { H2, Text } from 'ds/components/typography';
import { typographyFontSizeCfg } from 'ds/components/typography/types';
import config from 'config';
import { Link as LinkNav } from 'components/link';

export const SectionHeader = styled.div<{ withTitle: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ withTitle }) => withTitle ? 'space-between' : 'flex-end'};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  white-space: nowrap;
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    flex-direction: column;
    align-items: baseline;
  `}
`;

export const SectionTitleH2 = styled(H2)`
  ${({ theme }) => `
    && {
      font-size: ${typographyFontSizeCfg.h2.mobile}px;
      color: ${theme.colors.neutrals.grey1};
    }
  `}
`;

// Search section
export const SearchBlockRoot = styled.div`
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    background-color: ${colorToAlpha(theme.colors.neutrals.black, 0.27)};
  `};
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding: 19px ${theme.spacing(2)} ${theme.spacing(2)};
    border-radius: 0 0 ${theme.spacing(1.5)} ${theme.spacing(1.5)};
    margin-top: ${theme.spacing(6)};
  `}
  ${props => props.theme.media.showAt({ from: 2 })`max-width: 568px;`}
  background-color: ${props => colorToAlpha(props.theme.colors.neutrals.black, 0.4)};
  width: 100%;
  display: flex;
  z-index: 9;
  position: relative;
  ${({ theme }) => `
    padding: ${theme.spacing(0.5)};
    border-radius: ${theme.borderRadius.medium} 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
  `}
`;

export const SearchTypeSelectWrapper = styled(Text.withComponent('div'))`
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    width: 100%;
    text-align: center;
    padding-top: ${theme.spacing(2)};
    height: 48px;
    top: -48px;
    background-color: ${colorToAlpha(theme.colors.neutrals.black, 0.4)};
    left: 0;
    label:first-of-type {
      margin-right: 7px;
    }
    label:last-of-type {
      margin-left: 9px;
    }
  `};
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    right: 0;
  `};
  background-color: ${({ theme }) => colorToAlpha(theme.colors.neutrals.black, 0.27)};
  height: 32px;
  line-height: 32px;
  top: -32px;
  position: absolute;
  ${({ theme }) => `
    color: ${theme.colors.neutrals.white};
    border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0;
    padding: 0 ${theme.spacing(1.5)};
  `}

  label {
    font-weight: bold;
    color: ${({ theme }) => colorToAlpha(theme.colors.neutrals.white, 0.5)};
    padding: 6px 0;
    position: relative;
    cursor: pointer;

  }

  input[type='radio'] {
    display: none;
  }

  input:checked + label {
    color: ${({ theme }) => theme.colors.neutrals.white};
    margin: 0;
    ::after {
      content: '';
      display: block;
      height: 2px;
      width: 100%;
      position: absolute;
      bottom: 0;
      left: 0;
      ${({ theme }) => `
        border-radius: ${theme.borderRadius.micro};
        background-color: ${theme.colors.neutrals.white};
      `}
    }
  }
`;

export const InputWrapper = styled.div<{ isError?: boolean; isMobile: boolean; }>`
  flex: 1;

  .textfield-input {
    height: 48px;
    padding-top: 5px;
    padding-bottom: 5px;
    border: 0 none;
    ${({ theme }) => theme.media.showAt({ from: 2 })`width: 440px;`};
    ${({ theme, isMobile }) => theme.media.showAt({ at: 1 })`
      height: 48px;
      border: ${isMobile ? '0 none' : `2px solid ${theme.colors.neutrals.white}`}
    `};
    ${({ theme }) => `
      border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
    `}
  }
  ${({ isError }) => isError ? `
    input::placeholder {
      color: #D0021B;
      font-weight: 600;
    }
  ` : ''}
`;

export const SearchTypeSelectLabel = styled.span`
  ${({ theme }) => theme.media.showAt({ at: 1 })`padding-left: ${theme.spacing(2)};`};
  padding-left: ${({ theme }) => theme.spacing(3)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutrals.grey10};
`;

export const Separator = styled.span`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey6};
    padding: 0 ${theme.spacing(1)};
  `}
`;

export const SearchButton = styled.button`
  font-family: NovemberHebrew;
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding: 0 14px;
    width: 108px;
  `};
  svg {
    path {
      fill: ${({ theme }) => theme.colors.neutrals.white};
    }
  }
  border: none;
  height: 48px;
  line-height: 48px;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
  width: 120px;
  align-items: center;
  justify-content: center;
  display: flex;
  ${({ theme }) => `
    color: ${theme.colors.neutrals.white};
    background-color: ${theme.colors.brand.madlanGreen};
    border-radius: ${theme.borderRadius.small} 0 0 ${theme.borderRadius.small};
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    :hover {
      background-color:${colorToAlpha(theme.colors.brand.madlanGreen, 0.9)};
    }
    :focus {
      outline: none;
    }
  `};
`;


// Banner section
const bannerBG = `${config.imagesRoot}homepage/v3/cover-new.gif`;

export const BannerWrapper = styled.div<{ fixed: boolean; showFixedBanner: boolean }>`
  height: 72px;
  background-image: url("${bannerBG}");
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.white};
  cursor: pointer;

  ${({ theme }) => theme.media.showAt({ from: 1, to: 2 })`width: calc(100% - 48px);`}
  ${({ theme }) => theme.media.showAt({ at: 3 })`width: 864px;`}
  ${({ theme }) => theme.media.showAt({ from: 4 })`width: 1144px;`}
  ${({ theme }) => `
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
  `}
  ${({ fixed, showFixedBanner }) => fixed ? `
    opacity: ${showFixedBanner ? 1 : 0};
    transition: opacity 0.3s ease-out;
    position: fixed;
    bottom: 32px;
    top: auto;
    z-index: 10;
    transform: translateX(-50%) translateY(0);
  ` : ''}
`;

export const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    path {
      fill: ${({ theme }) => theme.colors.neutrals.white};
    }
  }
`;
export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: ${({ theme }) => theme.spacing(3)};
`;

export const BannerStyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.neutrals.white};
  cursor: pointer;
  :hover {
    color: ${({ theme }) => theme.colors.neutrals.grey10};
  }
`;


// Bulletin section
export const Row = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(8)} 0;
`;

export const ListingCardsItemWrapper = styled.div`
  ${props => props.theme.media.showAt({ at: 1 })`flex-basis: 280px;`};
  ${props => props.theme.media.showAt({ at: 2 })`flex-basis: 340px;`};
  ${props => props.theme.media.showAt({ from: 3, to: 4 })`flex-basis: 288px;`};
  ${props => props.theme.media.showAt({ at: 5 })`flex-basis: 322px;`};
`;

export const CardContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const EmploymentAreasCarouselSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: ${({ theme }) => theme.spacing(9)} auto;

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    margin-top: ${theme.spacing(4)};
    margin-bottom: ${theme.spacing(5)};
  `};

  ${({ theme }) => theme.media.showAt({ at: 3 })`max-width: 864px;`};
  ${({ theme }) => theme.media.showAt({ from: 4 })`max-width: 1144px;`};
`;

export const EmploymentAreasCarouselSectionHeader = styled.div`
  display: flex;
  align-self: flex-start;

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding-right: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(1.5)};
  `};

  ${({ theme }) => theme.media.showAt({ at: 2 })`
    padding-right: ${theme.spacing(6)};
    margin-bottom: ${theme.spacing(1.5)};
  `};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    margin-bottom: -${theme.spacing(1.5)};
  `};
`;

export const ViewPagerWrapper = styled.div`
  width : 100%;

  ${({ theme }) => theme.media.showAt({ to: 1 })`
    margin-right ${theme.spacing(3)};
  `};

  ${({ theme }) => theme.media.showAt({ from: 2 })`
    margin-right: ${theme.spacing(9)};
  `};
`;

export const CarouselWrapper = styled.div`
  ${({ theme }) => theme.media.showAt({ at: 3 })`max-width: 864px;`};
  ${({ theme }) => theme.media.showAt({ from: 4 })`max-width: 1144px;`};
`;

export const CarouselItem = styled(Text.withComponent(LinkNav))`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  gap: 8px;
  margin-left: ${({ theme }) => theme.spacing(1)};
  line-height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(3)}`};
  border-radius: 32px;

  &:hover {
    color: ${({ theme }) => theme.colors.brand.main};
    stroke: ${({ theme }) => theme.colors.brand.main};
    stroke-width: 0.5px;
    cursor: pointer;
    border-color: ${({ theme }) => theme.colors.neutrals.grey6};
  }
`;
